import MainHeading from "@/components/common/MainHeading"
import Layout from "@/components/layout/Layout"
import { SiteService } from "@/services/Site/SiteServive"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ThrowError } from "@/api/callApi"
import { showToastNotification } from "@/utility/helper"
import type { TableColumn } from "@/components/common/CommonTable"
import { Button } from "@/components/ui/button"
import { RoutesLink } from "@/utility/RouteLinks"
import { Pencil, Plus, Trash, TriangleAlert } from "lucide-react"
import CommonTable from "@/components/common/CommonTable"
import CommonModal from "@/components/common/CommonModal"
import type { site } from "@/interface/site.interface"

const Site: React.FC = () => {
    const navigate = useNavigate()
    const [sites, setSites] = useState<site[]>([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteId, setDeleteId] = useState<string>("");

    const handleGetSites = async () => {
        try {
            const response = await SiteService?.getSite();
            if (response?.status) {
                setSites(response?.data?.site)
            }
        } catch (error) {
            ThrowError(error)
        }
    }

    const handleDeleteSite = async () => {
        try {
            const response = await SiteService?.deleteSite(deleteId);
            if (response?.status) {
                showToastNotification("success", response?.message)
                toggleDeleteDialog("")
                handleGetSites();
            } else {
                showToastNotification("danger", response?.message)
            }
        } catch (error) {
            ThrowError(error)
        }
    }

    useEffect(() => {
        handleGetSites();
    }, []);

    const toggleDeleteDialog = (id: string | null) => {
        setDeleteId(id ?? "");
        setDeleteDialogOpen(!deleteDialogOpen);
    }

    const columns: TableColumn<site>[] = [
        {
            key: 'srNo',
            header: 'Sr No',
            render: (_item: site, index: number) => (
                <span className="text-muted-foreground font-medium"># {index + 1}</span>
            )
        },
        {
            key: 'name',
            header: 'Site Name',
            render: (item: site) => (
                <span className="font-semibold capitalize">{item?.name}</span>
            )
        },
        {
            key: 'type',
            header: 'Type',
            render: (item: site) => (
                <span className="capitalize">{item?.type}</span>
            )
        },
        {
            key: 'searchedLocation',
            header: 'Location',
            render: (item: site) => (
                <span className="text-muted-foreground">{item?.searchedLocation}</span>
            )
        },
        {
            key: 'range',
            header: 'Range (m)',
            render: (item: site) => (
                <span className="font-medium">{item?.range}</span>
            )
        },
        {
            key: 'isPunchAllowed',
            header: 'Punch Allowed',
            render: (item: site) => (
                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${item?.isPunchAllowed ? 'text-green-600 border-green-600 bg-green-50' : 'text-destructive border-destructive bg-destructive/10'}`}>
                    {item?.isPunchAllowed ? 'Yes' : 'No'}
                </span>
            )
        },
        {
            key: 'status',
            header: 'Status',
            render: (item: site) => (
                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${item?.status ? 'text-green-600 border-green-600 bg-green-50' : 'text-destructive border-destructive bg-destructive/10'}`}>
                    {item?.status ? 'Active' : 'Inactive'}
                </span>
            )
        },
        {
            key: 'actions',
            header: 'Actions',
            render: (item: site) => (
                <div className="flex gap-2">
                    <Button
                        size={'icon'}
                        onClick={() => navigate(`${RoutesLink.editSite}/${item?.id}`)}
                        className="bg-primary/10 text-primary hover:bg-primary/20"
                        title="Edit Site"
                    >
                        <Pencil size={18} />
                    </Button>
                    <Button
                        size={'icon'}
                        onClick={() => toggleDeleteDialog(item?.id ?? null)}
                        className="bg-destructive/10 text-destructive hover:bg-destructive/20"
                        title="Delete Site"
                    >
                        <Trash size={18} />
                    </Button>
                </div>
            ),
        },
    ]

    return (
        <>
            <Layout>
                <div className="space-y-6">
                    <MainHeading
                        title="Sites"
                        isBackButtonVisible={true}
                        buttonText='Add Site'
                        buttonIcon={<Plus />}
                        onClick={() => { navigate(RoutesLink.addSite) }}
                    />
                    <CommonTable data={sites} columns={columns} />
                </div>
            </Layout>

            <CommonModal
                title=""
                show={deleteDialogOpen}
                onClose={() => toggleDeleteDialog("")}
                className="max-w-md rounded-2xl p-0 overflow-hidden"
            >
                <div className="p-6">
                    <div className="flex flex-col items-center text-center">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                            <TriangleAlert className="text-red-600 w-8 h-8" />
                        </div>
                        <h2 className="mt-5 text-2xl font-bold text-gray-900">
                            Delete Site?
                        </h2>
                        <p className="mt-3 text-sm leading-6 text-gray-500">
                            This action cannot be undone. Once deleted, all site data will be
                            permanently removed from our servers.
                        </p>
                    </div>
                    <div className="mt-8 flex gap-3">
                        <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => toggleDeleteDialog("")}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            className="flex-1"
                            onClick={handleDeleteSite}
                        >
                            Delete
                        </Button>
                    </div>
                </div>
            </CommonModal>
        </>
    )
}

export default Site;